import { useContext } from "react";
import { SessionContext } from "../provider/SessionProvider";
import { ApiResponse, AuthResponse } from "../models/ApiResponse";
import { BASE_API } from "../hooks/http";
import { AuthorizationI } from "../../../apple-sign-in";
import { useChatbotIndexedDB } from "../hooks/chatbotIndexeddb";
import { useUnsyncChatIndexedDB } from "../hooks/unsyncChatIndexeddb";
import { EmailSignUpRequest } from "../models/EmailSignUpRequest";
import { PasswordRestoreRequest } from "../models/PasswordRestoreRequest";

export interface AppleAuthenticateRequest extends AuthorizationI {
    full_name: string;
}

export const useAuth = () => {
    const chatBotIndexedDB = useChatbotIndexedDB();
    const unsyncIndexedDB = useUnsyncChatIndexedDB();
    const session = useContext(SessionContext);

    if (!session.updateSession) {
        throw new Error('useSession must be used within a SessionProvider');
    }

    const _revokeRefreshToken = async (): Promise<any> => {
        return fetch(`${BASE_API}/patientapi/auth/revoke`, { method: "POST" });
    };

    const googleLogin = async (credential: string, accept_terms?: boolean): Promise<void> => {
        const url = `${BASE_API}/patientapi/auth/google`;
        return fetch(
            url,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ credential, accept_terms })
            })
            .then((response) => response.json())
            .then(async (data: ApiResponse<AuthResponse>) => {
                if (!data.ok) {
                    throw data;
                }

                chatBotIndexedDB.clearChatMessages();
                unsyncIndexedDB.clearUnsyncMessages();
                session?.updateSession?.({ accessToken: data.data!.access_token, refreshToken: data.data!.refresh_token! });
            });
    };

    const appleLogin = async (authorization: AppleAuthenticateRequest, accept_terms?: boolean): Promise<void> => {

        const url = `${BASE_API}/patientapi/auth/apple`;
        return fetch(
            url,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...authorization, accept_terms })
            })
            .then((response) => response.json())
            .then(async (data: ApiResponse<AuthResponse>) => {
                if (!data.ok) {
                    throw data;
                }

                chatBotIndexedDB.clearChatMessages();
                unsyncIndexedDB.clearUnsyncMessages();
                session?.updateSession?.({ accessToken: data.data!.access_token, refreshToken: data.data!.refresh_token! });
            });
    };

    const emailVerificationForSignup = async (email: string): Promise<string> => {
        const url = `${BASE_API}/patientapi/auth/email/verification`;
        return fetch(
            url,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email })
            })
            .then((response) => response.json())
            .then(async (data: ApiResponse<string>) => {
                if (!data.ok || !data.data) {
                    throw data;
                }

                return data.data;
            });
    };

    const emailSignUp = async (request: EmailSignUpRequest): Promise<void> => {
        const url = `${BASE_API}/patientapi/auth/email/signup`;
        return fetch(
            url,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(request)
            })
            .then((response) => response.json())
            .then(async (data: ApiResponse<AuthResponse>) => {
                if (!data.ok) {
                    throw data;
                }

                chatBotIndexedDB.clearChatMessages();
                unsyncIndexedDB.clearUnsyncMessages();
                session?.updateSession?.({ accessToken: data.data!.access_token, refreshToken: data.data!.refresh_token! });
            });
    };

    const emailLogin = async (email: string, password: string): Promise<void> => {
        const url = `${BASE_API}/patientapi/auth/email`;
        return fetch(
            url,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, password })
            })
            .then((response) => response.json())
            .then(async (data: ApiResponse<AuthResponse>) => {
                if (!data.ok) {
                    throw data;
                }

                chatBotIndexedDB.clearChatMessages();
                unsyncIndexedDB.clearUnsyncMessages();
                session?.updateSession?.({ accessToken: data.data!.access_token, refreshToken: data.data!.refresh_token! });
            });
    };

    const emailVerificationForPasswordReset = async (email: string): Promise<string> => {
        const url = `${BASE_API}/patientapi/auth/email/password-reset`;
        return fetch(
            url,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email })
            })
            .then((response) => response.json())
            .then(async (data: ApiResponse<string>) => {
                if (!data.ok || !data.data) {
                    throw data;
                }

                return data.data;
            });
    };

    const confirmResetPassword = async (request: PasswordRestoreRequest): Promise<void> => {
        const url = `${BASE_API}/patientapi/auth/email/password-reset/confirm`;
        return fetch(
            url,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(request)
            })
            .then((response) => response.json())
            .then(async (data: ApiResponse) => {
                if (!data.ok) {
                    throw data;
                }
            });
    };

    const refreshToken = async (): Promise<void> => {
        const access_token = session?.session?.current?.accessToken;
        const refresh_token = session?.session?.current?.refreshToken;

        if (!access_token || !refresh_token) {
            throw new Error("Cannot refresh token without a refresh token");
        }

        const url = `${BASE_API}/patientapi/auth/refresh`;
        return fetch(
            url,
            {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ access_token, refresh_token })
            })
            .then((response) => response.json())
            .then(async (data: ApiResponse<AuthResponse>) => {
                if (!data.ok) {
                    throw data;
                }

                session?.updateSession?.({ accessToken: data.data!.access_token, refreshToken: session?.session?.current?.refreshToken ?? "" });
            });
    };

    const logout = async (revoke: boolean = true): Promise<void> => {
        if (revoke) {
            await _revokeRefreshToken()
                .catch((error) => {
                    console.error('Error signing out', error);
                });
        }

        chatBotIndexedDB.clearChatMessages();
        unsyncIndexedDB.clearUnsyncMessages();
        session?.clearSession?.();
        location.replace('/');
    };

    return {
        googleLogin,
        appleLogin,
        emailVerificationForSignup,
        emailSignUp,
        emailLogin,
        emailVerificationForPasswordReset,
        confirmResetPassword,
        refreshToken,
        logout
    };
};
